import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Container,
  Grid,
  Group,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconArrowRight,
  IconBrandWindows,
  IconCalendarDue,
  IconFileCertificate,
  IconFileText,
  IconGavel,
  IconLock,
  IconMail,
  IconPhone,
  IconReportMoney,
  IconShoppingCart,
  IconUsers,
} from "@tabler/icons-react";
import { publicTenderApi, type PublicTender } from "../api/publicTenders";
import { Logo } from "../components/Logo";
import { BRAND_DARK, BRAND_MEDIUM } from "../theme/brand";
import { useAuth } from "../auth/AuthContext";


const CATEGORY_LABELS: Record<string, string> = {
  GOODS: "Goods",
  SERVICES: "Services",
  WORKS: "Works",
};

const MODULES = [
  {
    icon: IconGavel,
    title: "Tenders",
    body: "Publish tenders, receive bids and run technical and financial evaluations.",
  },
  {
    icon: IconUsers,
    title: "Bid committee",
    body: "Schedule meetings, record attendance and sign award sheets and return forms.",
  },
  {
    icon: IconFileText,
    title: "Single source & PR-03",
    body: "Prepare single source justifications and project summary sheets for approval.",
  },
  {
    icon: IconFileCertificate,
    title: "Contracts",
    body: "Track contract value, consumption, extensions and upcoming expiry dates.",
  },
  {
    icon: IconShoppingCart,
    title: "RFQs & purchase orders",
    body: "Request quotations, compare responses and raise purchase orders.",
  },
  {
    icon: IconReportMoney,
    title: "Approvals",
    body: "See everything waiting on you, in one queue, with a full audit trail.",
  },
];

/** Deadline wording with text (not colour alone) so urgency is clear to everyone. */
function deadlineInfo(deadline: string): { label: string; color: string } {
  const days = dayjs(deadline).startOf("day").diff(dayjs().startOf("day"), "day");
  if (days < 0) return { label: "Closed", color: "gray" };
  if (days === 0) return { label: "Closes today", color: "red" };
  if (days === 1) return { label: "Closes tomorrow", color: "red" };
  if (days <= 7) return { label: `Closes in ${days} days`, color: "orange" };
  return { label: `Closes in ${days} days`, color: BRAND_MEDIUM };
}

function TenderRow({ tender }: { tender: PublicTender }) {
  const deadline = deadlineInfo(tender.submissionDeadline);
  return (
    <Card
      component={Link}
      to={`/public/tenders/${tender.id}`}
      withBorder
      radius="md"
      padding="md"
      className="transition-shadow duration-200 hover:shadow-md"
    >
      <Group justify="space-between" wrap="nowrap" gap="md">
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Group gap="xs">
            <Text size="xs" c="dimmed" fw={500}>
              {tender.referenceNo}
            </Text>
            <Badge variant="light" color="gray" radius="sm" size="xs">
              {CATEGORY_LABELS[tender.category] ?? tender.category}
            </Badge>
          </Group>
          <Text size="sm" fw={600} lineClamp={1}>
            {tender.description}
          </Text>
          <Text size="xs" c="dimmed">
            Deadline {dayjs(tender.submissionDeadline).format("D MMM YYYY, HH:mm")}
          </Text>
        </Stack>
        <Badge variant="light" color={deadline.color} radius="sm" style={{ flexShrink: 0 }}>
          {deadline.label}
        </Badge>
      </Group>
    </Card>
  );
}

export const Landing: React.FC = () => {
  const { user, loginWithMicrosoft } = useAuth();

  const { data: tenders, isLoading, isError, refetch } = useQuery({
    queryKey: ["public-tenders-landing"],
    queryFn: () => publicTenderApi.getPublishedTenders({ page: 1, limit: 5 }),
  });

  return (
    <div className="min-h-screen bg-[var(--mantine-color-body)]">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded focus:bg-white focus:px-4 focus:py-2 focus:shadow"
      >
        Skip to main content
      </a>

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-[var(--mantine-color-default-border)] bg-[var(--mantine-color-body)]">
        <Container size="lg" py="sm">
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <Logo height={40} />
              <Text fw={700} size="lg" c={BRAND_DARK} visibleFrom="xs">
                HMH Procurement
              </Text>
            </Group>
            {user ? (
              <Button component={Link} to="/dashboard" color={BRAND_MEDIUM} rightSection={<IconArrowRight size={16} />}>
                Dashboard
              </Button>
            ) : (
              <Button component={Link} to="/login" color={BRAND_MEDIUM}>
                Sign in
              </Button>
            )}
          </Group>
        </Container>
      </header>

      <main id="main">
        {/* Hero + sign in */}
        <section style={{ background: `linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND_MEDIUM} 100%)` }}>
          <Container size="lg" py={{ base: 48, md: 88 }}>
            <Grid gutter={{ base: 32, md: 64 }} align="center">
              <Grid.Col span={{ base: 12, md: 7 }}>
                <Stack gap="lg">
                  <Text size="sm" fw={600} c="rgba(255,255,255,0.85)" tt="uppercase" lts={1}>
                    Hulhumalé Hospital · Staff portal
                  </Text>
                  <Title order={1} c="white" fz={{ base: 32, md: 46 }} lh={1.15}>
                    Procurement, from request to contract
                  </Title>
                  <Text size="lg" c="rgba(255,255,255,0.9)" maw={540}>
                    Run tenders, bid committees, approvals and contracts in one system — with every decision recorded
                    and every document in one place.
                  </Text>
                </Stack>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 5 }}>
                <Card radius="md" padding="xl" shadow="lg">
                  {user ? (
                    <Stack gap="md">
                      <Text size="sm" c="dimmed">
                        Signed in as
                      </Text>
                      <Title order={2} size="h3">
                        {user.fullName}
                      </Title>
                      <Button
                        component={Link}
                        to="/dashboard"
                        size="md"
                        color={BRAND_MEDIUM}
                        fullWidth
                        rightSection={<IconArrowRight size={18} />}
                      >
                        Go to your dashboard
                      </Button>
                    </Stack>
                  ) : (
                    <Stack gap="md">
                      <Title order={2} size="h3">
                        Sign in
                      </Title>
                      <Text size="sm" c="dimmed">
                        Use your hospital Microsoft account.
                      </Text>
                      <Button
                        size="md"
                        color={BRAND_MEDIUM}
                        fullWidth
                        leftSection={<IconBrandWindows size={18} />}
                        onClick={loginWithMicrosoft}
                      >
                        Sign in with Microsoft
                      </Button>
                      <Group gap={6} wrap="nowrap">
                        <IconLock size={14} color="var(--mantine-color-dimmed)" aria-hidden />
                        <Text size="xs" c="dimmed">
                          For authorised Hulhumalé Hospital staff only.
                        </Text>
                      </Group>
                      <Anchor component={Link} to="/login" size="sm">
                        Other sign-in options
                      </Anchor>
                    </Stack>
                  )}
                </Card>
              </Grid.Col>
            </Grid>
          </Container>
        </section>

        <Container size="lg" py={{ base: 48, md: 72 }}>
          <Grid gutter={{ base: 32, md: 48 }}>
            {/* What's in the system */}
            <Grid.Col span={{ base: 12, md: 7 }}>
              <section aria-labelledby="modules-heading">
                <Title id="modules-heading" order={2} mb={4}>
                  Everything in one place
                </Title>
                <Text c="dimmed" mb="lg">
                  What you can do once you sign in.
                </Text>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                  {MODULES.map((m) => (
                    <Group key={m.title} gap="md" wrap="nowrap" align="flex-start">
                      <ThemeIcon size={40} radius="md" variant="light" color={BRAND_MEDIUM}>
                        <m.icon size={22} aria-hidden />
                      </ThemeIcon>
                      <Stack gap={2}>
                        <Text fw={600}>{m.title}</Text>
                        <Text size="sm" c="dimmed">
                          {m.body}
                        </Text>
                      </Stack>
                    </Group>
                  ))}
                </SimpleGrid>
              </section>
            </Grid.Col>

            {/* Published tenders (live) */}
            <Grid.Col span={{ base: 12, md: 5 }}>
              <section aria-labelledby="tenders-heading">
                <Group justify="space-between" align="flex-end" mb="lg">
                  <Stack gap={4}>
                    <Title id="tenders-heading" order={2} size="h3">
                      Published tenders
                    </Title>
                    <Text size="sm" c="dimmed">
                      {isLoading
                        ? "Loading…"
                        : `${tenders?.meta.total ?? 0} currently visible to suppliers`}
                    </Text>
                  </Stack>
                  <Anchor component={Link} to="/public/tenders" size="sm" fw={500} style={{ whiteSpace: "nowrap" }}>
                    <Group gap={4} wrap="nowrap">
                      View all
                      <IconArrowRight size={14} aria-hidden />
                    </Group>
                  </Anchor>
                </Group>

                {isLoading ? (
                  <Stack gap="sm" aria-busy="true" aria-label="Loading tenders">
                    {[0, 1, 2].map((i) => (
                      <Skeleton key={i} h={84} radius="md" />
                    ))}
                  </Stack>
                ) : isError ? (
                  <Alert color="red" title="Couldn't load tenders">
                    <Group gap="sm">
                      <Text size="sm">Check your connection and try again.</Text>
                      <Button size="xs" variant="light" color="red" onClick={() => refetch()}>
                        Retry
                      </Button>
                    </Group>
                  </Alert>
                ) : tenders?.data.length ? (
                  <Stack gap="sm">
                    {tenders.data.map((t) => (
                      <TenderRow key={t.id} tender={t} />
                    ))}
                  </Stack>
                ) : (
                  <Card withBorder radius="md" padding="lg">
                    <Stack align="center" gap="xs" py="sm">
                      <ThemeIcon size={44} radius="xl" variant="light" color="gray">
                        <IconCalendarDue size={24} aria-hidden />
                      </ThemeIcon>
                      <Text fw={600}>No published tenders</Text>
                      <Text size="sm" c="dimmed" ta="center">
                        Tenders appear here once they are published.
                      </Text>
                    </Stack>
                  </Card>
                )}
              </section>
            </Grid.Col>
          </Grid>
        </Container>
      </main>

      {/* Footer */}
      <footer style={{ background: BRAND_DARK }}>
        <Container size="lg" py={40}>
          <Grid gutter="xl">
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack gap="sm">
                <Text fw={700} size="lg" c="white">
                  HMH Procurement
                </Text>
                <Text size="sm" c="rgba(255,255,255,0.8)" maw={380}>
                  Internal procurement management system for Hulhumalé Hospital.
                </Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack gap="xs">
                <Text fw={600} c="white">
                  Need help?
                </Text>
                <Group gap={6} wrap="nowrap">
                  <IconMail size={16} color="rgba(255,255,255,0.8)" aria-hidden />
                  <Anchor href="mailto:procurement@hmh.mv" size="sm" c="rgba(255,255,255,0.85)">
                    procurement@hmh.mv
                  </Anchor>
                </Group>
                <Group gap={6} wrap="nowrap">
                  <IconPhone size={16} color="rgba(255,255,255,0.8)" aria-hidden />
                  <Text size="sm" c="rgba(255,255,255,0.85)">
                    Internal extension 1234 · 8AM – 5PM
                  </Text>
                </Group>
                <Text size="sm" c="rgba(255,255,255,0.85)">
                  Sign-in problems: contact the IT Department.
                </Text>
              </Stack>
            </Grid.Col>
          </Grid>
          <Text
            size="xs"
            c="rgba(255,255,255,0.7)"
            mt="xl"
            pt="lg"
            style={{ borderTop: "1px solid rgba(255,255,255,0.15)" }}
          >
            © {new Date().getFullYear()} Hulhumalé Hospital. Internal use only.
          </Text>
        </Container>
      </footer>
    </div>
  );
};
